import React from 'react';
import { LegalList, LegalNotice, LegalP, LegalPage, LegalSection } from '../components/Legal/LegalPage';

const Terms: React.FC = () => {
  const updated = 'May 14, 2026';

  return (
    <LegalPage
      title="Terms & Conditions"
      updated={updated}
      intro="These terms govern access to RideSaathi, a platform for organizing ride requirements for drivers and fleet operators in India."
    >
      <LegalNotice severity="warning">
        RideSaathi is only a platform for organizing rides. We do not guarantee ride booking, payment, passenger or driver safety, driver or customer verification, ride completion, fare collection, or any offline transaction.
      </LegalNotice>

      <LegalSection title="1. Acceptance of terms">
        <LegalP>
          By creating an account, browsing listings, unlocking contact details, buying a plan, or using any RideSaathi service, you agree to these Terms and Conditions, the Privacy Policy, Refund Policy, Community Policy, and any product rules shown in the app.
        </LegalP>
        <LegalP>
          RideSaathi is operated by Chahal Travel. The platform is intended for drivers, fleet operators, contributors, and transport-related businesses in India who are at least 18 years old.
        </LegalP>
      </LegalSection>

      <LegalSection title="2. Platform role">
        <LegalP>
          RideSaathi aggregates and organizes ride requirements from authorized sources, verified contributors, connected ride information channels, ride lead submissions, public or permitted ride requirements, and platform-generated or contributor-submitted rides.
        </LegalP>
        <LegalP>
          RideSaathi is not a taxi operator, transport company, travel agent, payment guarantor, insurer, employment agency, or emergency service. Users are responsible for evaluating each lead and conducting their own checks before accepting any ride.
        </LegalP>
      </LegalSection>

      <LegalSection title="3. Accounts and eligibility">
        <LegalList
          items={[
            'You must provide accurate account, phone, email, vehicle, and billing information where requested.',
            'You are responsible for all activity under your account and for keeping your password and devices secure.',
            'You must not impersonate another person, create fake accounts, share account access without permission, or bypass plan limits.',
            'We may suspend, restrict, or terminate accounts for misuse, payment issues, repeated invalid reports, illegal activity, or violation of these terms.',
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Lead access and contact details">
        <LegalP>
          Rides may include route, timing, vehicle preference, fare estimate, and contact information where applicable. Contact details may be masked publicly and may only be visible to verified or logged-in users where applicable.
        </LegalP>
        <LegalP>
          You may use unlocked contact details only for the relevant ride requirement. You must not resell, publish, misuse, spam, harass, or redistribute contact details.
        </LegalP>
      </LegalSection>

      <LegalSection title="5. User responsibilities">
        <LegalList
          items={[
            'Use RideSaathi only for lawful, ride-related purposes.',
            'Check route, fare, customer identity, vehicle requirements, permits, local rules, and safety conditions before accepting a ride.',
            'Do not use automated tools to extract, copy, bulk-download, reverse engineer, or overload the platform.',
            'Do not post fake ride requirements, spam, abusive content, misleading contact details, harassment, or illegal transport activity.',
            'Report incorrect, expired, unauthorized, misleading, or unsafe listings through support or the reporting tools where available.',
          ]}
        />
      </LegalSection>

      <LegalSection title="6. Subscriptions, credits, and payments">
        <LegalP>
          Paid plans, top-ups, and unlock credits are priced in Indian Rupees unless stated otherwise. Plan benefits, lead limits, contact visibility, early access, and features may vary by plan.
        </LegalP>
        <LegalP>
          Payments are processed through enabled third-party payment providers. RideSaathi does not store card, UPI PIN, or net banking credentials.
        </LegalP>
        <LegalP>
          Refunds are handled according to our Refund Policy. Subscription payments are generally non-refundable once access is provided, except for duplicate payments or technical billing issues that we approve after review.
        </LegalP>
      </LegalSection>

      <LegalSection title="7. Lead accuracy and reporting">
        <LegalP>
          We use reasonable operational checks to organize ride requirements, reduce duplicates, and remove irrelevant or expired listings. We do not guarantee that every listing is current, available, accurate, safe, payable, or suitable for you.
        </LegalP>
        <LegalP>
          RideSaathi may edit, mask, expire, remove, reject, or restrict listings that appear incorrect, expired, unauthorized, abusive, spammy, misleading, unlawful, or against platform policy.
        </LegalP>
      </LegalSection>

      <LegalSection title="8. Third-party platforms">
        <LegalP>
          RideSaathi does not claim partnership, sponsorship, endorsement, or affiliation with WhatsApp, Meta, Google, Apple, or any third-party platform unless officially stated in writing by RideSaathi and the relevant third party.
        </LegalP>
        <LegalP>
          Your use of third-party payment gateways, browsers, operating systems, app stores, messaging apps, maps, and communication services is also subject to their separate terms and policies.
        </LegalP>
      </LegalSection>

      <LegalSection title="9. Intellectual property">
        <LegalP>
          RideSaathi software, design, branding, content, workflows, and platform materials are owned by RideSaathi, Chahal Travel, or their licensors. You may not copy, modify, sell, publish, or distribute them without written permission.
        </LegalP>
      </LegalSection>

      <LegalSection title="10. Limitation of liability">
        <LegalP>
          To the maximum extent permitted by law, RideSaathi and Chahal Travel are not liable for indirect, incidental, special, consequential, punitive, or offline transaction-related losses arising from your use of the platform.
        </LegalP>
        <LegalP>
          Our total liability for any claim will not exceed the amount paid by you to RideSaathi in the three months before the event giving rise to the claim.
        </LegalP>
      </LegalSection>

      <LegalSection title="11. Governing law">
        <LegalP>
          These terms are governed by the laws of India. Subject to mandatory law, disputes will be handled by courts having jurisdiction in Punjab, India.
        </LegalP>
      </LegalSection>

      <LegalSection title="12. Contact">
        <LegalP>
          Questions about these terms can be sent to <strong>support@ridesaathi.in</strong> or through the Contact / Grievance Officer page.
        </LegalP>
      </LegalSection>
    </LegalPage>
  );
};

export default Terms;
